import { useState } from 'react';
import { Button, Textarea } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useRejectNegotiation,
  useResolveNegotiation,
  useCancelNegotiation,
  useArchiveNegotiation,
  type ContractNegotiationDetail,
} from '@/api/contract-negotiations';

type ActionKind = 'reject' | 'resolve' | 'cancel' | 'archive' | null;

/**
 * Status-gated lifecycle actions for a contract negotiation.
 * - Reject / Cancel: require `reason` (backend binding:"required")
 * - Resolve: requires `resolution_notes` (backend binding:"required")
 * - Archive: no body required
 *
 * Visible only for non-terminal statuses (completed, rejected, cancelled,
 * archived, converted are terminal — no further actions allowed).
 */
export function NegotiationActions({ detail }: { detail: ContractNegotiationDetail }) {
  const toast = useToast();
  const [open, setOpen] = useState<ActionKind>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);

  const reject = useRejectNegotiation(detail.contract_negotiation_id);
  const resolve = useResolveNegotiation(detail.contract_negotiation_id);
  const cancel = useCancelNegotiation(detail.contract_negotiation_id);
  const archive = useArchiveNegotiation(detail.contract_negotiation_id);

  const status = detail.contract_negotiation_status;
  const isTerminal = ['completed', 'rejected', 'cancelled', 'converted'].includes(status);

  // Archive is available from any non-archived, non-converted status.
  const canArchive = !['archived', 'converted'].includes(status);

  if (isTerminal && !canArchive) return null;

  const close = () => {
    setOpen(null);
    setReason('');
    setReasonError(false);
  };

  const onError = (label: string) => (err: unknown) => {
    const message = err instanceof Error ? err.message : `Failed to ${label} negotiation`;
    toast.error(`Failed to ${label} negotiation`, { description: message });
  };
  const onDone = (label: string) => () => {
    toast.success(`Negotiation ${label}`);
    close();
  };

  const reasonField = (label: string, placeholder: string) => (
    <Field
      label={label}
      error={reasonError ? `${label} is required.` : undefined}
    >
      {(fieldProps) => (
        <Textarea
          {...fieldProps}
          rows={3}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (reasonError) setReasonError(false);
          }}
          placeholder={placeholder}
        />
      )}
    </Field>
  );

  const runWithReason = (kind: 'reject' | 'cancel') => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setReasonError(true);
      return;
    }
    const mutation = kind === 'reject' ? reject : cancel;
    const label = kind === 'reject' ? 'rejected' : 'cancelled';
    mutation.mutate(
      { reason: trimmed },
      { onSuccess: onDone(label), onError: onError(kind) },
    );
  };

  const runResolve = () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setReasonError(true);
      return;
    }
    resolve.mutate(
      { resolution_notes: trimmed },
      { onSuccess: onDone('resolved'), onError: onError('resolve') },
    );
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
        {!isTerminal && (
          <>
            <Button size="sm" variant="primary" onClick={() => setOpen('resolve')}>
              Resolve
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setOpen('reject')}>
              Reject
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setOpen('cancel')}>
              Cancel
            </Button>
          </>
        )}
        {canArchive && (
          <Button size="sm" variant="secondary" onClick={() => setOpen('archive')}>
            Archive
          </Button>
        )}
      </div>

      {/* Resolve */}
      <ConfirmDialog
        open={open === 'resolve'}
        onOpenChange={(o) => (o ? setOpen('resolve') : close())}
        title="Resolve negotiation"
        description="Mark this negotiation as completed with resolution notes."
        confirmLabel="Resolve"
        loading={resolve.isPending}
        onConfirm={runResolve}
      >
        {reasonField('Resolution notes', 'Describe how the negotiation was resolved')}
      </ConfirmDialog>

      {/* Reject */}
      <ConfirmDialog
        open={open === 'reject'}
        onOpenChange={(o) => (o ? setOpen('reject') : close())}
        title="Reject negotiation"
        description="Provide a reason for rejecting this negotiation."
        confirmLabel="Reject"
        destructive
        loading={reject.isPending}
        onConfirm={() => runWithReason('reject')}
      >
        {reasonField('Rejection reason', 'Reason for rejection')}
      </ConfirmDialog>

      {/* Cancel */}
      <ConfirmDialog
        open={open === 'cancel'}
        onOpenChange={(o) => (o ? setOpen('cancel') : close())}
        title="Cancel negotiation"
        description="Provide a reason for cancelling this negotiation."
        confirmLabel="Cancel"
        destructive
        loading={cancel.isPending}
        onConfirm={() => runWithReason('cancel')}
      >
        {reasonField('Cancellation reason', 'Reason for cancellation')}
      </ConfirmDialog>

      {/* Archive */}
      <ConfirmDialog
        open={open === 'archive'}
        onOpenChange={(o) => (o ? setOpen('archive') : close())}
        title="Archive negotiation"
        description="Archive this negotiation. It will be marked as archived and hidden from active lists."
        confirmLabel="Archive"
        loading={archive.isPending}
        onConfirm={() =>
          archive.mutate(undefined, {
            onSuccess: onDone('archived'),
            onError: onError('archive'),
          })
        }
      />
    </>
  );
}
